import { TutorialConfig, TutorialStep, TutorialTask } from "../types";

const IMMICH_TASKS: TutorialTask[] = [
  {
    id: "task-1",
    runId: "0ab9a812-afc6-40fd-8f03-ed27c50ba11c",
    name: "Change language to traditional Chinese",
    type: "success",
    description: "Learn to identify obvious successful completions",
  },
  {
    id: "task-2",
    runId: "cacfb032-bce8-4322-9fb4-6ec7e4245a16",
    name: "Add photos to Easter Fun album",
    type: "failure",
    description: "Learn to identify obvious failures",
  },
  {
    id: "task-3",
    runId: "c474d279-dae3-440a-ac58-b92c0c7d0aa9",
    name: "Delete blue Olympic Rings photo",
    type: "fake_failure",
    description: "Success that looks like failure due to extra bumbling",
  },
  {
    id: "task-4",
    runId: "fb9f5b11-a690-4959-b2ca-a8efef332e89",
    name: "Delete Olympic Rings photo (with extra deletion)",
    type: "fake_success",
    description: "Failure that looks like success due to additional modifications",
  },
  {
    id: "task-5",
    runId: "be6aaf66-e964-4f33-b2c1-131370e4ba59",
    name: "Disable notifications",
    type: "success",
    description: "Success that requires understanding UI behavior",
  },
  {
    id: "task-6",
    runId: "bc1023f5-4991-4e7c-9a6c-5c72847e52e4",
    name: "Delete more zoomed-in ring photo",
    type: "success",
    description: "Task requiring playground verification",
  },
  {
    id: "task-7",
    runId: "fa01b63b-4fe5-436b-b6d1-855cd8ceb482",
    name: "Search for photos without changing state",
    type: "bad_task",
    description: "Understand why tasks that don't change database state are bad",
  },
];

const IMMICH_STEPS: TutorialStep[] = [
  // Platform navigation instructions
  {
    id: "welcome",
    title: "Welcome to Immich Evaluation!",
    content:
      "This tutorial will show you how to evaluate Immich model runs. First, let's learn how to use the platform.",
    targetSelector: ".App-header h1",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-description",
    title: "Task Description",
    content:
      "This shows the instructions given to the AI model to complete inside Immich. Read this carefully to understand what the model was asked to do.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "skip-to-frame-2",
    title: "Skip to Frame 2",
    content:
      "Click the right arrow button or press the RIGHT arrow key to go to the next frame.",
    targetSelector: ".nav-button:nth-child(3)",
    action: "seek",
    position: "top",
  },
  {
    id: "current-action",
    title: "Current Action",
    content:
      "This section describes what action the AI model is performing in the current frame. It helps you understand what the model is trying to do.",
    targetSelector: ".sidebar-section:nth-child(3)",
    action: "next",
    position: "right",
  },
  {
    id: "main-click",
    title: "Main Click Indicator",
    content:
      "The circle with ripple effect shows where the model clicked on the screen in this frame. This helps you track the model's interactions.",
    targetSelector: ".click-indicator",
    action: "next",
    position: "left",
  },
  {
    id: "next-click",
    title: "Next Click Preview",
    content:
      "The faded circle shows where the model will click in the next frame. This helps you anticipate the model's next action.",
    targetSelector: ".next-click-indicator",
    action: "next",
    position: "left",
  },
  {
    id: "go-to-frame-3",
    title: "Go to Next Frame",
    content:
      'Click the right arrow button to advance to frame 3. Notice how the previous "next click" becomes the current click.',
    targetSelector: ".nav-button:nth-child(3)",
    action: "seek",
    position: "top",
  },
  {
    id: "seek-backward",
    title: "Previous Frame",
    content:
      "Click the left arrow button or press the LEFT arrow key to go back to the previous frame.",
    targetSelector: ".nav-button:first-child",
    action: "seek",
    position: "top",
  },
  {
    id: "play",
    title: "Play the Video",
    content: "Click the play button or press SPACE to start playing the video.",
    targetSelector: ".play-pause-btn",
    action: "play",
    position: "top",
  },
  {
    id: "pause",
    title: "Pause the Video",
    content: "Click the pause button or press SPACE to pause the video.",
    targetSelector: ".play-pause-btn",
    action: "pause",
    position: "top",
  },
  {
    id: "speed-controls",
    title: "Speed Controls",
    content:
      "Use the speed buttons to control playback speed. This helps you carefully review complex actions.",
    targetSelector: ".speed-selector",
    action: "next",
    position: "top",
  },
  {
    id: "seek-timeline",
    title: "Timeline Scrubbing",
    content:
      "Click and drag on the timeline to quickly navigate to any frame. The blue bar shows your current position. Try it now, then click Next when ready.",
    targetSelector: ".timeline-container",
    action: "next",
    position: "top",
  },
  {
    id: "watch-before-eval",
    title: "Watch Before Evaluating",
    content:
      "Important: You must watch the entire video before evaluating. Let's jump to the final frame to see how evaluation works.",
    targetSelector: ".sidebar-section:nth-child(2)",
    action: "next",
    position: "right",
  },
  {
    id: "finalframe",
    title: "Jump to Final Frame",
    content:
      "Click on the timeline near the end to jump to the final frame. This shows the end result of the AI's work.",
    targetSelector: ".timeline-container",
    action: "next",
    position: "top",
  },

  // Task 1: Obvious Success (Change language)
  {
    id: "task-1-intro",
    title: "Example 1: Obvious Success",
    content:
      "Let's start with a clear success example. The AI is asked to change the language to traditional Chinese.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-1-watch",
    title: "Watch for Efficient Navigation",
    content:
      "The AI very efficiently navigates to the settings page and clearly changes the language to traditional Chinese without any additional modifications.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-1-evaluate",
    title: "Evaluate This Success",
    content:
      "The AI successfully changed the language setting as requested. No extra actions were taken. Click Success to mark this correctly.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "success",
  },
  {
    id: "task-1-complete",
    title: "Good Job!",
    content:
      'That\'s correct! This was an obvious success. Click "Next task" to continue.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },

  // Task 2: Obvious Failure (Add photos to album)
  {
    id: "task-2-intro",
    title: "Example 2: Obvious Failure",
    content:
      "Now let's see a clear failure. The AI is asked to add all photos taken on April 20th, 2014 to the new \"Easter Fun\" album.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-2-watch",
    title: "Observe the Failure",
    content:
      "By the end of this run, it's obvious that the AI got nowhere close to adding all of the photos taken on April 20th, 2014 to the new \"Easter Fun\" album.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-2-evaluate",
    title: "Evaluate This Failure",
    content:
      "The AI clearly failed to complete the task. This is an obvious failure. Click Fail to mark it correctly.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "fail",
  },
  {
    id: "task-2-complete",
    title: "Correct!",
    content:
      'You identified the obvious failure correctly. Click "Next task" to continue.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },

  // Task 3: Success that looks like failure (Delete Olympic Rings)
  {
    id: "task-3-intro",
    title: "Example 3: Success That Looks Like Failure",
    content:
      "This task asks the AI to delete the blue photo of the Olympic Rings taken on June 29th, 2011. Watch carefully!",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-3-watch",
    title: "Watch the Entire Video",
    content:
      "The AI correctly deletes the blue photo in frames 46-47, but then it proceeds to bumble around for another 50 frames for no good reason.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-3-important",
    title: "No Lasting Effect",
    content:
      "Notice that none of this additional bumbling had a lasting effect on the Immich database. It was just the AI clicking around and truly doing nothing.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-3-evaluate",
    title: "Evaluate Correctly",
    content:
      "Since the AI completed the task without any additional modifications, this video should be labeled a success! Click Success.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "success",
  },
  {
    id: "task-3-complete",
    title: "Well Done!",
    content:
      'You correctly identified this as a success despite the extra bumbling. Click "Next task" to continue.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },

  // Task 4: Failure that looks like success (Delete Olympic Rings with extra deletion)
  {
    id: "task-4-intro",
    title: "Example 4: Failure That Looks Like Success",
    content:
      "This task also asks to delete the blue photo of the Olympic Rings taken on June 29th, 2011. Watch the entire video carefully!",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-4-watch",
    title: "Watch for Multiple Deletions",
    content:
      "Here, the AI correctly deletes the blue photo of the Olympic Rings taken on June 29th, 2011 in frames 45-46, but then it also deletes different photos of Olympic Rings in frames 51-52 and another photo in frames 62-63. This additional modification makes the video a clear failure. Remember to watch the entire video closely for any extra modifications!",
    targetSelector: ".screenshot-container",
    action: "next",
    position: "left",
  },
  {
    id: "task-4-evaluate",
    title: "Final Evaluation",
    content:
      "The AI made an additional modification beyond what was requested. This is a failure. Click Fail.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "fail",
  },
  {
    id: "task-4-complete",
    title: "Good Analysis!",
    content:
      'You correctly caught the extra deletion. Click "Next task" to continue.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },

  // Task 5: Success that requires understanding UI behavior (Notifications)
  {
    id: "task-5-intro",
    title: "Example 5: Understanding UI Behavior",
    content:
      "This task asks to disable notifications including \"Album added\" and \"Album updated\" notifications. Watch how the AI handles this.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-5-watch",
    title: "Watch the Notification Toggle",
    content:
      "The AI doesn't explicitly toggle off the \"Album added\" and \"Album updated\" notifications individually. This might look like a failure at first...",
    targetSelector: ".screenshot-container",
    action: "next",
    position: "left",
  },
  {
    id: "task-5-explanation",
    title: "Read the Toggle Descriptions",
    content:
      "These are just specific types of email notifications and the AI already turned off all email notifications with the top toggle!",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-5-ui-limitation",
    title: "UI Design Limitation",
    content:
      "The UI actually doesn't even let you toggle off \"Album added\" and \"Album updated\" after turning off the main \"enable email notifications\" toggle. You can check in the playground and see for yourself!",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-5-evaluate",
    title: "Evaluate Based on Task Completion",
    content:
      "The AI successfully disabled all notifications as requested. This is a success! Click Success.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "success",
  },
  {
    id: "task-5-complete",
    title: "Excellent!",
    content:
      'You correctly understood the UI behavior and task completion. Click "Next task" to continue.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },

  // Task 6: Using the playground for verification
  {
    id: "task-6-intro",
    title: "Example 6: Using the Immich Playground",
    content:
      "This task asks the AI to delete the \"more zoomed-in\" photo of a ring taken at Bridgeport. We'll need to use the playground to verify.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-6-watch",
    title: "Watch the Deletion",
    content:
      "It is clear from the video that the AI did indeed delete a photo of a pretty zoomed in ring taken at Bridgeport.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-6-ambiguity",
    title: "The Verification Challenge",
    content:
      "We don't see a second photo to compare it to in the video. Was it actually the \"more zoomed-in\" one?",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-6-playground",
    title: "Using the Playground",
    content:
      "If you open up the Immich playground and filter to just photos taken at Bridgeport, you can clearly see both pictures of the ring and observe that the AI did delete the more zoomed-in one.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-6-evaluate",
    title: "Evaluate Based on Verification",
    content:
      "Through playground verification, we confirmed this video is indeed a success. Click Success.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "success",
  },
  {
    id: "task-6-complete",
    title: "Great Job!",
    content:
      'You learned when and how to use the playground for verification! Click "Next task" to continue.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },

  // Task 7: Bad task (doesn't change database state)
  {
    id: "task-7-intro",
    title: "Example 7: Bad Task - No State Change",
    content:
      "This task asks the AI to search for and display photos. While this might seem like a realistic task, there's an important issue.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-7-watch",
    title: "Watch the Search",
    content:
      "The AI searches for photos and displays them, but notice that it doesn't actually change anything in the Immich database.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-7-explanation",
    title: "Why This is a Bad Task",
    content:
      "We are only interested in tasks that change the actual state of the underlying Immich database. Even though this is a realistic task someone might want to do in Immich, it's bad because just searching and displaying photos does not actually change the state of the underlying database.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-7-good-tasks",
    title: "Examples of Good Tasks",
    content:
      "However, basically any other task you can think of probably does change the database! Whether it's creating albums, deleting photos, hearting photos, changing settings, naming people, etc etc etc.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-7-evaluate",
    title: "Evaluate This Bad Task",
    content:
      "Since this task doesn't change the database state, it's a bad task. Click Bad to mark it correctly.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "bad-task",
  },
  {
    id: "task-7-complete",
    title: "Excellent Understanding!",
    content:
      'You correctly identified why this is a bad task. Click "Next task" to continue.',
    targetSelector: "button.next-task",
    action: "next",
    position: "top",
  },
  {
    id: "additional-reminders",
    title: "Important Patterns to Remember",
    content:
      "Watch for: 1) Creating extra items (like the Speech album failure where TWO albums were created), 2) Any additional modifications beyond the request.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "key-reminders",
    title: "Key Reminders",
    content:
      "1) Always watch entire videos\n2) Look for ANY additional modifications\n3) Use the playground when needed to verify\n4) Understand UI limitations",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "tutorial-complete",
    title: "Tutorial Complete!",
    content:
      "Congratulations! You've learned to evaluate Immich tasks including obvious cases, deceptive cases, and those requiring playground verification. Click below to start evaluating real Immich tasks. Click on 'New Projects' to claim your first labeling project.",
    action: "next",
    position: "bottom",
  },
];

export const immichTutorialConfig: TutorialConfig = {
  software: "immich",
  displayName: "Immich",
  totalTasks: 7,
  tasks: IMMICH_TASKS,
  steps: IMMICH_STEPS,
  completionKey: "immich_tutorial_completed",
  dataSource: "api",
  // No custom fetcher needed - will use standard API calls
};
